import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Icon,
  Label,
  Modal,
  Pagination,
  Statistic,
  Table,
} from 'semantic-ui-react';
import { API, showError, timestamp2string } from '../helpers';
import { renderQuota } from '../helpers/render';
import { ITEMS_PER_PAGE } from '../constants';

function getColorByElapsedTime(elapsedTime) {
  if (elapsedTime === undefined || elapsedTime === 0) return 'grey';
  if (elapsedTime < 1000) return 'green';
  if (elapsedTime < 3000) return 'olive';
  if (elapsedTime < 5000) return 'yellow';
  if (elapsedTime < 10000) return 'orange';
  return 'red';
}

const TokenUsageModal = ({ token, onClose }) => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [stat, setStat] = useState({ quota: 0 });
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(1);

  const loadStat = async () => {
    try {
      const res = await API.get(
        `/api/log/self/stat?type=2&token_name=${token.name}`
      );
      const { success, data } = res.data;
      if (success) {
        setStat(data);
      }
    } catch (error) {
      showError(error);
    }
  };

  const loadLogs = async (startIdx) => {
    setLoading(true);
    try {
      const res = await API.get(
        `/api/log/self/?p=${startIdx}&type=2&token_name=${token.name}`
      );
      const { success, message, data } = res.data;
      if (success) {
        if (startIdx === 0) {
          setLogs(data);
        } else {
          let newLogs = [...logs];
          newLogs.splice(startIdx * ITEMS_PER_PAGE, data.length, ...data);
          setLogs(newLogs);
        }
      } else {
        showError(message);
      }
    } catch (error) {
      showError(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadStat();
    loadLogs(0);
  }, [token.name]);

  const onPaginationChange = (e, { activePage }) => {
    (async () => {
      if (activePage === Math.ceil(logs.length / ITEMS_PER_PAGE) + 1) {
        await loadLogs(activePage - 1);
      }
      setActivePage(activePage);
    })();
  };

  return (
    <Modal open onClose={onClose} size='large'>
      <Modal.Header>
        <Icon name='chart bar' />
        {t('token.usage_modal.title')}：{token.name}
      </Modal.Header>
      <Modal.Content scrolling>
        <Statistic.Group size='small' widths='one'>
          <Statistic>
            <Statistic.Value>
              {renderQuota(stat.quota, t, 2)}
            </Statistic.Value>
            <Statistic.Label>
              <Icon name='payment' />
              {t('token.usage_modal.total_usage')}
            </Statistic.Label>
          </Statistic>
        </Statistic.Group>

        <Table basic='very' compact size='small' style={{ marginTop: '1em' }}>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>
                {t('token.usage_modal.time')}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {t('token.usage_modal.model')}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {t('token.usage_modal.quota')}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {t('token.usage_modal.prompt_tokens')}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {t('token.usage_modal.completion_tokens')}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {t('token.usage_modal.elapsed')}
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {logs.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan='6' textAlign='center'>
                  {loading
                    ? t('token.usage_modal.loading')
                    : t('token.usage_modal.no_records')}
                </Table.Cell>
              </Table.Row>
            )}
            {logs.map((log, idx) => (
              <Table.Row key={log.id || idx}>
                <Table.Cell>
                  {timestamp2string(log.created_at)}
                </Table.Cell>
                <Table.Cell>
                  <Label basic size='mini'>
                    {log.model_name || '-'}
                  </Label>
                </Table.Cell>
                <Table.Cell>{renderQuota(log.quota, t, 2)}</Table.Cell>
                <Table.Cell>{log.prompt_tokens ?? '-'}</Table.Cell>
                <Table.Cell>{log.completion_tokens ?? '-'}</Table.Cell>
                <Table.Cell>
                  {log.elapsed_time !== undefined ? (
                    <Label
                      basic
                      size='mini'
                      color={getColorByElapsedTime(log.elapsed_time)}
                    >
                      {log.elapsed_time} ms
                    </Label>
                  ) : (
                    '-'
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
          <Table.Footer>
            <Table.Row>
              <Table.HeaderCell colSpan='6'>
                <Pagination
                  floated='right'
                  activePage={activePage}
                  onPageChange={onPaginationChange}
                  size='mini'
                  siblingRange={1}
                  totalPages={
                    Math.ceil(logs.length / ITEMS_PER_PAGE) +
                    (logs.length % ITEMS_PER_PAGE === 0 ? 1 : 0)
                  }
                />
              </Table.HeaderCell>
            </Table.Row>
          </Table.Footer>
        </Table>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>
          {t('token.usage_modal.close')}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default TokenUsageModal;
